import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DataTable,
  DateDisplay,
  EnumValueDisplay,
  IDataTableColumn,
  useFormEvents,
} from '@lib/common';
import { FormStatus } from '@lib/state';
import { Box, Button, Grid, Tooltip, Typography } from '@material-ui/core';
import { CorporateInvoiceForms, corporateInvoiceService, InvoiceStatus } from 'app/state';
import React, { useCallback, useEffect, useMemo } from 'react';
import { renderInvoiceNumberCell } from '../render-invoice-number-cell';

interface Props {
  runId: string;
  statuses: Array<InvoiceStatus>;
  draft: boolean;
  runComplete?: boolean;
}

function renderCell(value?: string | null) {
  return <Typography>{value}</Typography>;
}

function renderCostCenter(value?: string | null, error?: string | null) {
  return (
    <Tooltip title={error ?? ''} placement="bottom-start">
      <Grid container spacing={1}>
        <Grid item>{error && <FontAwesomeIcon icon="info-circle" color="error" />}</Grid>
        <Grid item>
          <Typography>{value}</Typography>
        </Grid>
      </Grid>
    </Tooltip>
  );
}

const getColumns = (numberColWidth: number): Array<IDataTableColumn<InvoiceStatus>> => [
  {
    title: 'Invoice No.',
    valueFactory: status =>
      renderInvoiceNumberCell(status.invoiceNumber, {
        message: status.isDeleted ? ' (Deleted)' : '',
        tooltip: status.nonRegenerative ? 'This invoice cannot be regenerated.' : '',
        error: status.nonRegenerative,
      }),
    colSpan: numberColWidth,
  },
  {
    title: 'Status',
    valueFactory: status => (
      <>
        <EnumValueDisplay value={status.createInvoiceStatus} />
        <Typography>&nbsp;{'@'}&nbsp;</Typography>
        <DateDisplay date={new Date(status.createdDate ?? '')} dateFormat="M/d/yy" />
        <DateDisplay date={new Date(status.createdDate ?? '')} dateFormat="h:mm:ss a" />
      </>
    ),
    colSpan: 1.5,
  },
  {
    title: 'Status Message',
    valueFactory: status => renderCell(status.statusMessage),
  },
  {
    title: 'Corporate Account',
    valueFactory: status => renderCell(status.corporateAccount),
  },
  {
    title: 'Location',
    valueFactory: status => renderCell(status.location),
  },
  {
    title: 'Cost Center 1',
    valueFactory: status => renderCostCenter(status.costCenter1, status.costCenter1Errors),
  },
  {
    title: 'Cost Center 2',
    valueFactory: status => renderCostCenter(status.costCenter2, status.costCenter2Errors),
  },
  {
    title: 'Crew',
    valueFactory: status => renderCostCenter(status.crew, status.crewErrors),
  },
];

export function InvoiceStatusList({ runId, statuses, draft, runComplete }: Props) {
  const [selectedInvoices, setSelectedInvoices] = React.useState<string[]>([]);
  const [{ status }, reset] = useFormEvents(CorporateInvoiceForms.Regenerate);

  const columns = useMemo(() => {
    const invoiceNumbers = statuses.map(s => s.invoiceNumber);
    const longestInvoiceLength = Math.max(...invoiceNumbers.map(n => n?.toString().length ?? 0));
    const numberColWidth = Math.max(longestInvoiceLength / 17, 1); //Set minimum width to 1

    return getColumns(Math.min(numberColWidth, 2)); //Max width of 2
  }, [statuses]);

  const onRegenerateInvoices = useCallback(() => {
    corporateInvoiceService.regenerateInvoice({
      runId,
      invoiceStatusIds: selectedInvoices,
      isDraft: draft,
    });
    setSelectedInvoices([]);
  }, [selectedInvoices, runId, setSelectedInvoices, draft]);

  useEffect(() => {
    if (status === FormStatus.Success) {
      setSelectedInvoices([]);
      reset();
    }
  }, [status, reset]);

  return (
    <Box mt={2}>
      {draft && (
        <Grid item style={{ padding: '1em' }}>
          <Button
            onClick={onRegenerateInvoices}
            variant="outlined"
            disabled={selectedInvoices.length === 0}
          >
            Regenerate Selected Invoices
          </Button>
        </Grid>
      )}
      <DataTable
        items={statuses}
        columns={columns}
        getItemKey={status => status.id ?? ''}
        isRowSelectable={r => draft && !r.nonRegenerative && !!runComplete}
        selectionModel={statuses
          .filter(f => selectedInvoices.includes(f.id ?? ''))
          .map(s => s.id ?? '')}
        onSelectionModelChange={model => setSelectedInvoices(model.map(x => x as string))}
        checkboxSelection={draft}
      ></DataTable>
      {draft && (
        <Grid item style={{ padding: '1em' }}>
          <Button
            onClick={onRegenerateInvoices}
            variant="outlined"
            disabled={selectedInvoices.length === 0}
          >
            Regenerate Selected Invoices
          </Button>
        </Grid>
      )}
    </Box>
  );
}
